<template>
  <div class="grid grid-cols-1 m-8 bg-white shadow-md p-4 gap-4">
    <div class="grid grid-cols-1 md:grid-cols-2 gap-4 w-full">
      <div class="grid grid-cols-1 gap-4 w-[400px]">
        <h2 class="col-span-1 text-lg flex items-center gap-2">
          <span class="border-black border-b-2">{{ t('insuranceInfo') }}</span>
          <router-link :to="editUrl">
            <font-awesome-icon icon="edit" />
          </router-link>
        </h2>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-4 px-4">
          <div class="font-bold">{{ t('name') }}:</div>
          <div class="col-span-1">{{ initInsurance.name }}</div>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-4 px-4">
          <div class="font-bold">{{ t('contactDisplayName') }}:</div>
          <div class="col-span-1">
            {{ initInsurance.contactDisplayName }}
          </div>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-4 px-4">
          <div class="font-bold">{{ t('phone') }}:</div>
          <div class="col-span-1" style="direction: ltr">
            {{ initInsurance.phone }}
          </div>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-4 px-4">
          <div class="font-bold">{{ t('email') }}:</div>
          <div class="col-span-1">
            {{ initInsurance.email }}
          </div>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-4 px-4">
          <div class="font-bold">{{ t('streetAddress') }}:</div>
          <div class="col-span-1">
            {{ initInsurance.streetAddress }}
          </div>
        </div>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4 px-4">
          <div class="font-bold">{{ t('createdDate') }}:</div>
          <div class="col-span-1">
            {{ formatPostgresDate(initInsurance.createdAt) }}
          </div>
        </div>
      </div>
      <div class="self-start grid grid-cols-1 gap-4 w-80">
        <h2 class="col-span-1 text-lg flex items-center gap-2">
          <span class="border-black border-b-2">{{ t('ordersHistory') }}</span>
        </h2>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-4 px-4">
          <div class="font-bold col-span-1">{{ t('totalOrders') }}:</div>
          <div class="col-span-1">
            {{totalPurchases}}
          </div>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-4 px-4">
          <div class="font-bold col-span-1">{{ t('totalAmount') }}:</div>
          <div class="col-span-1">
            {{ toDinero(totalDueAmount, currency, precision) }}
          </div>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-4 px-4">
          <div class="font-bold col-span-1">{{ t('totalBalance') }}:</div>
          <div class="col-span-1">
            {{ toDinero(balance, currency, precision) }}
          </div>
        </div>
      </div>
    </div>

    <div class="flex justify-end">
            <div class="md:w-2/12 m-2">
              <horizontal-button
                :title="t('accountStatement')"
                @click.prevent="
                  $router.push({
                    name: 'insurance-account-statement',
                    params: { insurance_id: initInsurance.id }
                  })
                "
              />
            </div>
    </div>

    <div class="col-span-full">
      <h2 class="col-span-1 md:col-span-2 text-lg flex items-center gap-2">
        <span class="border-black border-b-2">{{ t('attachments') }}</span>
      </h2>
      <InsuranceAttachmentsManager :data="initInsurance"></InsuranceAttachmentsManager>
    </div>

    <div class="col-span-full border-t-2 pt-2">
      <h2 class="col-span-1 md:col-span-2 text-lg flex items-center gap-2">
        <span class="border-black border-b-2">{{ t('relatedOrders') }}</span>
      </h2>
      <InsuranceOrders @collectivePayment="() => {
        loadInsurance();
      }"/>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, onMounted, ref } from 'vue'

import Insurance, { insuranceI18nMessages } from '@/models/insurance/Insurance'
import { useI18n } from 'vue-i18n'
import { Route as $Route } from '@/router/routes'
import { useRoute, useRouter } from 'vue-router'
import { useHTTP } from '@/plugins/http'
import { formatPostgresDate } from '@/utils/date'
import Dinero, { Currency } from 'dinero.js'
import { formatDinero } from '@/utils/money'
import InsuranceOrders from './InsuranceOrders.vue'
import {CustomerInvoice} from "@/models/sales/CustomerInvoice";
import HorizontalButton from '@/components/HorizontalButton.vue'
import InsuranceAttachmentsManager
  from "@/components/file-manager/InsuranceAttachmentsManager.vue";

const { t } = useI18n({
  messages: {
    en: {
      insuranceInfo: 'Insurance Info',
      noPhone: 'No Phone # was provided',
      noEmail: 'No Email was provided',
      ...insuranceI18nMessages.en,
      ordersHistory: 'Orders History',
      totalBalance: 'Total Balance',
      totalAmount: 'Total Amount',
      totalOrders: 'Total Orders',
      insurance: 'Insurance',
      billNo: 'Bill No',
      billDate: 'Bill Date',
      dueDate: 'Due Date',
      total: 'Total',
      amountPaid: 'Amount Paid',
      balance: 'Balance',
      cashier: 'Cashier',
      accountStatement: 'Account Statement',
      createdDate: 'Created Date',
      attachments: "Attachments",
      relatedOrders: "Related Orders"
    },
    ar: {
      insuranceInfo: 'بيانات شركة التأمين',
      noPhone: 'لا يوجد رقم هاتف لعرضه',
      noEmail: 'لا يوجد بريد الكتروني لعرضه',
      ...insuranceI18nMessages.ar,
      ordersHistory: 'تاريخ الطلبات',
      totalBalance: 'الرصيد الكلي',
      totalAmount: 'المبلغ الإجمالي',
      totalOrders: 'أجمالي الطلبات',
      insurance: 'شركة التأمين',
      billNo: 'رقم الفاتورة',
      billDate: 'تاريخ الفاتورة',
      dueDate: 'تاريخ الاستحقاق',
      amountPaid: 'المبلغ المدفوع',
      balance: 'الرصيد',
      cashier: 'الكاشير',
      total: 'المجموع',
      accountStatement: 'كشف الحساب',
      createdDate: 'تاريخ الانشاء',
      attachments: "المرفقات",
      relatedOrders: "الطلبات ذات صلة"
    }
  }
})

const $route = useRoute()
const $router = useRouter()
const $http = useHTTP()

const initInsurance = ref(new Insurance())
const initInvoices = ref<CustomerInvoice[]>([])
const balance = ref(0)
const totalDueAmount = ref(0)
const currency = ref('')
const precision = ref(0)
const totalPurchases = ref(0);

const editUrl = computed(() => {
  const id = $route.params.id
  return $Route.INSURANCES_INSURANCE.replace(':id', id as string)
})

async function loadInsurance() {
  const id = $route.params.id as string
  const url = `${Insurance.ENDPOINT}/${id}`
  const insurance = await $http.get<Insurance>(url)
  if (insurance) Object.assign(initInsurance.value, Insurance.from(insurance))

  const invoicesUrl = `accounting/customerinvoices?insurance_id=${id}`
  const { invoices, totalBalanceAmount } =  (await $http.get<{
    invoices: CustomerInvoice[]
    totalBalanceAmount: number
  }>(invoicesUrl)) || []

  initInvoices.value = invoices.map(i => CustomerInvoice.from(i))
  balance.value = totalBalanceAmount

  totalDueAmount.value = 0
  for (const invoice of initInvoices.value) {
    if (invoice.totalDueAmount) totalDueAmount.value += invoice.insuranceTotalDueAmount
    precision.value = invoice.precision
    currency.value = invoice.currency

    if (invoice.insuranceTotalDueAmount != 0) totalPurchases.value++
  }
}

function toDinero(amount: number, currency: any, precision: number) {
  let result = ''
  try {
    const dinero = Dinero({
      amount: amount,
      currency: (currency as Currency) || 'IQD',
      precision: precision
    })

    result = formatDinero(dinero)
  } catch (error) {
    console.log(error)
  }

  return result
}

onMounted(() => {
  loadInsurance()
})
</script>

<style></style>
